import React, { useState, useEffect, useRef } from "react";
import Reloj from "../Temporizador/Reloj";
import * as conf from "../../config/config";

const url = conf.api_raiz;

/**
 * Función para consultar el histórico de temporizadores
 * Migrada desde Monitoreo.react.js
 */
export const fetchTemporizadoresHistorico = async (idPlataformaTipo, idPlataforma, idMovimiento) => {
  try {
    const response = await fetch(
      url + "temporizadoresHistorico/movimiento/" + idPlataformaTipo + "/" + idPlataforma + "/" + idMovimiento,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + "Bearer " + sessionStorage.getItem("tok"),
        },
      }
    );
    
    const data = await response.json();
    
    if (data && data.length > 0) {
      // Marcar como vencidos para que el frontend los muestre con el estado correcto
      data.forEach((t) => { t.vencido = true; });
      return data;
    }
    
    return [];
  } catch (err) {
    console.log("Error al traer histórico de temporizadores:", err);
    return [];
  }
};

/**
 * Hook personalizado para manejar temporizadores históricos
 */
export const useTemporizadoresHistorico = (idPlataformaTipo, idPlataforma, idMovimiento) => {
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);

  const cargarHistorico = async () => {
    if (!idPlataformaTipo || !idPlataforma || !idMovimiento) return;
    
    setLoading(true);
    try {
      const data = await fetchTemporizadoresHistorico(idPlataformaTipo, idPlataforma, idMovimiento);
      setHistorico(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarHistorico();
  }, [idPlataformaTipo, idPlataforma, idMovimiento]);

  return { 
    historico, 
    loading, 
    recargarHistorico: cargarHistorico 
  };
};

/**
 * Componente legado para la plataforma 820.
 * Funciona como en el último commit de GitHub (formulario con fecha y hora).
 */
const RecordatorioLegacy = (props) => {
  const [fechaRecordatorio, setFechaRecordatorio] = useState("");
  const [horaRecordatorio, setHoraRecordatorio] = useState("");
  const [asunto, setAsunto] = useState("");
  const [recordatoriosLocales, setRecordatoriosLocales] = useState([]);

  const idPlataforma = props.movimiento.plataforma
    ? props.movimiento.plataforma.id
    : props.movimiento.idPlataforma
      ? props.movimiento.idPlataforma.id
      : null;
  const idMovimiento = props.movimiento.id;
  const idPlataformaTipo = props.movimiento.plataforma
    ? props.movimiento.plataforma.plataformaTipo.id
    : props.movimiento.idPlataforma
      ? props.movimiento.idPlataforma.plataformaTipo.id
      : null;

  const { historico, loading, recargarHistorico } = useTemporizadoresHistorico(idPlataformaTipo, idPlataforma, idMovimiento);

  /**
   * Función para agregar un recordatorio.
   */
  const crearRecordatorioHandler = (event) => {
    event.preventDefault();
    
    if (!fechaRecordatorio || !horaRecordatorio || !asunto) {
      alert("Por favor complete todos los campos");
      return;
    }

    const fechaCompleta = new Date(`${fechaRecordatorio}T${horaRecordatorio}`);
    const ahora = new Date();
    
    if (fechaCompleta <= ahora) {
      alert("La fecha y hora del recordatorio debe ser futura");
      return;
    }

    let elemento = {
      idMovimiento: props.movimiento.id,
      minutos: Math.ceil((fechaCompleta - ahora) / 60000),
      fechaRecordatorio: fechaCompleta,
      creacion: ahora,
      asunto: asunto,
      idUsuario: props.usuario,
      idPlataforma: props.movimiento.plataforma? props.movimiento.plataforma.id : props.movimiento.idPlataforma.id,
      placa: props.movimiento.placa,
      cartaPorte: props.movimiento.cartaPorte,
      idCorredor: props.movimiento.corredor? props.movimiento.corredor.id : props.movimiento.idCorredor.id
    };

    props.addToArray(elemento);
    
    setRecordatoriosLocales((prev) => [
      ...prev,
      <li className="list-group-item list-group-item-success border-left-success" key={`${elemento.idMovimiento}-${Date.now()}`}>
        <div>
          <h6 className="mb-1">
            <i className="fa fa-truck mr-2"></i>
            Movimiento: {elemento.idMovimiento}
          </h6>
          <p className="mb-1">
            <i className="fa fa-clock-o mr-2"></i>
            <strong>Recordatorio:</strong> {fechaCompleta.toLocaleString('es-ES')}
          </p>
          <p className="mb-1">
            <i className="fa fa-comment mr-2"></i>
            <strong>Motivo:</strong> {elemento.asunto}
          </p>
        </div>
      </li>
    ]);

    // Limpiar formulario
    setFechaRecordatorio("");
    setHoraRecordatorio("");
    setAsunto("");
  };

  /**
   * Función para abrir GPS
   */
  const abrirGPS = (recordatorio) => {
    if (props.abrirmodalGps) {
      props.abrirmodalGps();
    } else {
      alert(`Recordatorio para movimiento ${recordatorio.idMovimiento}\nMotivo: ${recordatorio.asunto}`);
    }
  };

  /**
   * Función para verificar recordatorios vencidos
   */
  const verificarRecordatorios = () => {
    const ahora = new Date();
    if (props.tiempos) {
      props.tiempos.forEach(tiempo => {
        if (tiempo.fechaRecordatorio && new Date(tiempo.fechaRecordatorio) <= ahora) {
          if (window.confirm(`Recordatorio: ${tiempo.asunto}\n¿Desea ver el GPS del movimiento ${tiempo.idMovimiento}?`)) {
            abrirGPS(tiempo);
          }
        }
      });
    }
  };

  /**
   * Preparar recordatorios existentes (solo del movimiento actual)
   */
  useEffect(() => {
    // Combinar temporizadores activos y histórico
    const todosLosTemporizadores = [...(props.tiempos || []), ...historico];
    
    if (todosLosTemporizadores.length > 0) {
      const tiemposFiltrados = todosLosTemporizadores.filter(
        (t) => t.idMovimiento === props.movimiento.id
      );
      setRecordatoriosLocales(
        tiemposFiltrados.map((tiempo) => {
          // Determinar si está vencido usando creacion + minutos
          let fechaCreacion;
          if (typeof tiempo.creacion === "string" && !tiempo.creacion.includes("T") && !tiempo.creacion.includes("Z") && !tiempo.creacion.includes("+")) {
            fechaCreacion = new Date(tiempo.creacion.replace(" ", "T"));
          } else {
            fechaCreacion = new Date(tiempo.creacion);
          }
          const ahora = new Date();
          const transcurridoMin = (ahora.getTime() - fechaCreacion.getTime()) / 60000;
          const esPasado = tiempo.vencido || transcurridoMin >= tiempo.minutos;

          // Calcular fecha de recordatorio para mostrar
          const fechaRec = new Date(fechaCreacion.getTime() + tiempo.minutos * 60000);
          
          return (
            <li 
              className={`list-group-item ${esPasado ? 'list-group-item-warning' : 'list-group-item-info'}`} 
              key={`${tiempo.idMovimiento}-${tiempo.idTemporizadores || tiempo.creacion}`}
            >
              <p><strong>Movimiento:</strong> {tiempo.idMovimiento}</p>
              <p><strong>Recordatorio:</strong> {fechaRec.toLocaleString()}</p>
              <p><strong>Estado:</strong> {esPasado ? 'Vencido' : 'Pendiente'}</p>
              <p><strong>Asunto:</strong> {tiempo.asunto}</p>
              {tiempo.vencido && (
                <span className="badge badge-secondary">Histórico</span>
              )}
            </li>
          );
        })
      );
    }

    // Verificar recordatorios cada minuto
    const interval = setInterval(verificarRecordatorios, 60000);
    return () => clearInterval(interval);
  }, [props.tiempos, historico]);

  // Obtener fecha mínima (hoy) en zona horaria local
  const hoy = new Date();
  const year = hoy.getFullYear();
  const month = String(hoy.getMonth() + 1).padStart(2, '0');
  const day = String(hoy.getDate()).padStart(2, '0');
  const fechaMinima = `${year}-${month}-${day}`;

  return (
    <>
      <form>
        <div className="row justify-content-center">
          <div className="col-md-8">
            <h5 className="text-center mb-3">Programar Recordatorio</h5>
            <small className="form-text text-muted text-center d-block mb-3">
              Seleccione una fecha y hora futura para recibir un recordatorio con acceso al GPS del movimiento.
            </small>
            
            <div className="form-group mb-3">
              <label htmlFor="fechaRecordatorio">
                Fecha del Recordatorio <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="date"
                className="form-control"
                id="fechaRecordatorio"
                value={fechaRecordatorio}
                min={fechaMinima}
                onChange={(e) => setFechaRecordatorio(e.target.value)}
              />
            </div>

            <div className="form-group mb-3">
              <label htmlFor="horaRecordatorio">
                Hora del Recordatorio <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="time"
                className="form-control"
                id="horaRecordatorio"
                value={horaRecordatorio}
                onChange={(e) => setHoraRecordatorio(e.target.value)}
              />
            </div>

            <div className="form-group mb-3">
              <label htmlFor="asunto">
                Motivo del Recordatorio <span style={{ color: "red" }}>*</span>
              </label>
              <textarea
                className="form-control"
                id="asunto"
                rows="3"
                value={asunto}
                placeholder="Ej: Verificar llegada a destino, Revisar documentación, etc."
                onChange={(e) => setAsunto(e.target.value)}
              />
            </div>

            <div className="text-center mb-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={crearRecordatorioHandler}
                disabled={!fechaRecordatorio || !horaRecordatorio || !asunto}
              >
                <i className="fa fa-calendar-plus-o mr-2"></i>
                Crear Recordatorio
              </button>
            </div>
          </div>
        </div>

        {recordatoriosLocales.length > 0 && (
          <div className="row justify-content-center">
            <div className="col-md-10">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6>Recordatorios:</h6>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={recargarHistorico}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fa fa-spinner fa-spin mr-1"></i>
                      Cargando...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-refresh mr-1"></i>
                      Actualizar Histórico
                    </>
                  )}
                </button>
              </div>
              <ul className="list-group">{recordatoriosLocales}</ul>
            </div>
          </div>
        )}
      </form>
    </>
  );
};

/**
 * Componente nuevo (mejorado) para todas las plataformas excepto la 820.
 * Usa temporizador con horas/minutos y reloj de cuenta regresiva.
 */
const RecordatorioNuevo = (props) => {
  const [minutos, setMinutos] = useState("00");
  const [horas, setHoras] = useState("00");
  const [asunto, setAsunto] = useState("");
  const [timeRemaining, setTimeRemaining] = useState(false);
  const [timePassed, setTimePassed] = useState(0);
  const [, forceUpdate] = useState(0);
  const intervalRef = useRef(null);

  const idPlataforma = props.movimiento.plataforma
    ? props.movimiento.plataforma.id
    : props.movimiento.idPlataforma
      ? props.movimiento.idPlataforma.id
      : null;
  const idMovimiento = props.movimiento.id;
  const idPlataformaTipo = props.movimiento.plataforma
    ? props.movimiento.plataforma.plataformaTipo.id
    : props.movimiento.idPlataforma
      ? props.movimiento.idPlataforma.plataformaTipo.id
      : null;

  const { historico, loading, recargarHistorico } = useTemporizadoresHistorico(idPlataformaTipo, idPlataforma, idMovimiento);

  /**
   * Función para limitar el campo de horas y darle formato.
   */
  const inputHoursHandler = (event) => {
    let numero = +event.target.value;
    if (numero <= 9) {
      setHoras(`0${numero}`);
    } else if (numero <= 24) {
      setHoras(`${numero}`);
    } else {
      setHoras("24");
    }
  };

  /**
   * Función para limitar el campo de minutos y darle formato.
   */
  const inputMinutesHandler = (event) => {
    let numero = +event.target.value;
    if (numero <= 9) {
      setMinutos(`0${numero}`);
    } else if (numero <= 59) {
      setMinutos(`${numero}`);
    } else {
      setMinutos("59");
    }
  };

  /**
   * Función para crear el recordatorio/temporizador.
   * La fecha se establece automáticamente como hoy + las horas y minutos ingresados.
   */
  const crearRecordatorioHandler = (event) => {
    event.preventDefault();

    const tiempoEnMinutos = +horas * 60 + +minutos;

    if (tiempoEnMinutos === 0) {
      alert("Por favor ingrese al menos 1 minuto");
      return;
    }

    if (!asunto.trim()) {
      alert("Por favor ingrese el motivo del recordatorio");
      return;
    }

    const ahora = new Date();
    const fechaRecordatorio = new Date(ahora.getTime() + tiempoEnMinutos * 60000);

    let elemento = {
      idMovimiento: props.movimiento.id,
      minutos: tiempoEnMinutos,
      fechaRecordatorio: fechaRecordatorio,
      creacion: ahora,
      asunto: asunto,
      idUsuario: props.usuario,
      idPlataforma: props.movimiento.plataforma
        ? props.movimiento.plataforma.id
        : props.movimiento.idPlataforma.id,
      placa: props.movimiento.placa,
      cartaPorte: props.movimiento.cartaPorte,
      idCorredor: props.movimiento.corredor
        ? props.movimiento.corredor.id
        : props.movimiento.idCorredor.id,
    };

    props.addToArray(elemento);

    // Activar reloj con cuenta regresiva
    setTimeRemaining(tiempoEnMinutos * 60);
    setTimePassed(0);

    // Limpiar formulario
    setMinutos("00");
    setHoras("00");
    setAsunto("");
  };

  /**
   * Genera la lista de recordatorios con su estado actualizado en tiempo real.
   * Solo muestra los temporizadores del movimiento actual.
   */
  const generarListaRecordatorios = () => {
    // Combinar temporizadores activos y histórico
    const todosLosTemporizadores = [...(props.tiempos || []), ...historico];
    
    if (todosLosTemporizadores.length === 0) return [];

    // Filtrar solo los temporizadores del movimiento actual
    const tiemposFiltrados = todosLosTemporizadores.filter(
      (t) => t.idMovimiento === props.movimiento.id
    );

    if (tiemposFiltrados.length === 0) return [];

    // Ordenar por fecha de creación descendente (más reciente primero)
    const tiemposOrdenados = [...tiemposFiltrados].sort((a, b) => {
      const parseDate = (d) => {
        if (typeof d === "string" && !d.includes("T") && !d.includes("Z") && !d.includes("+")) {
          return new Date(d.replace(" ", "T"));
        }
        return new Date(d);
      };
      return parseDate(b.creacion).getTime() - parseDate(a.creacion).getTime();
    });

    return tiemposOrdenados.map((tiempo) => {
      let fechaCreacion;
      if (typeof tiempo.creacion === "string" && !tiempo.creacion.includes("T") && !tiempo.creacion.includes("Z") && !tiempo.creacion.includes("+")) {
        fechaCreacion = new Date(tiempo.creacion.replace(" ", "T"));
      } else {
        fechaCreacion = new Date(tiempo.creacion);
      }
      const tiempoTotal = +tiempo.minutos;
      const transcurridoMin = parseFloat(
        ((new Date().getTime() - fechaCreacion.getTime()) / 60000).toFixed(1)
      );

      const esPasado = tiempo.vencido || transcurridoMin >= tiempoTotal;

      // Calcular tiempo restante para mostrar
      const restanteMin = Math.max(0, tiempoTotal - transcurridoMin);
      const restanteHoras = Math.floor(restanteMin / 60);
      const restanteMinutos = Math.floor(restanteMin % 60);

      return (
        <li
          className={`list-group-item ${
            esPasado ? "list-group-item-danger" : "list-group-item-info"
          }`}
          key={`${tiempo.idMovimiento}-${tiempo.idTemporizadores || tiempo.creacion}`}
        >
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <p className="mb-1">
                <strong>Movimiento:</strong> {tiempo.idMovimiento}
              </p>
              <p className="mb-1">
                <strong>Tiempo establecido:</strong> {tiempoTotal} minutos
              </p>
              <p className="mb-1">
                <strong>Tiempo transcurrido:</strong> {transcurridoMin} minutos
              </p>
              {!esPasado && (
                <p className="mb-1">
                  <strong>Tiempo restante:</strong> {restanteHoras}h {restanteMinutos}min
                </p>
              )}
              <p className="mb-1">
                <strong>Asunto:</strong> {tiempo.asunto}
              </p>
            </div>
            <div className="d-flex flex-column align-items-end">
              <span
                className={`badge ${
                  esPasado ? "badge-danger" : "badge-primary"
                } mb-1`}
                style={{ fontSize: "14px", padding: "8px 12px" }}
              >
                {esPasado ? "⚠️ Vencido" : "⏳ Activo"}
              </span>
              {tiempo.vencido && (
                <span className="badge badge-secondary">
                  Histórico
                </span>
              )}
            </div>
          </div>
        </li>
      );
    });
  };

  /**
   * Preparar recordatorios existentes y mostrar reloj si hay uno activo
   */
  useEffect(() => {
    // Combinar temporizadores activos y histórico
    const todosLosTemporizadores = [...(props.tiempos || []), ...historico];
    
    if (todosLosTemporizadores.length > 0) {
      // Buscar temporizador activo (no vencido) para este movimiento
      const tiempoActivo = todosLosTemporizadores.find(
        (t) => t.idMovimiento === props.movimiento.id && !t.vencido
      );

      if (tiempoActivo) {
        let fechaCreacionActivo;
        if (typeof tiempoActivo.creacion === "string" && !tiempoActivo.creacion.includes("T") && !tiempoActivo.creacion.includes("Z") && !tiempoActivo.creacion.includes("+")) {
          fechaCreacionActivo = new Date(tiempoActivo.creacion.replace(" ", "T"));
        } else {
          fechaCreacionActivo = new Date(tiempoActivo.creacion);
        }
        const transcurrido =
          (new Date().getTime() - fechaCreacionActivo.getTime()) /
          1000;
        const tiempoTotal = tiempoActivo.minutos * 60;

        if (transcurrido < tiempoTotal) {
          setTimeRemaining(Math.trunc(tiempoTotal));
          setTimePassed(Math.trunc(transcurrido));
        }
      }
    }

    // Actualizar la lista cada segundo para reflejar el estado en tiempo real
    intervalRef.current = setInterval(() => {
      forceUpdate((n) => n + 1);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [props.tiempos, historico]);

  // Generar lista actualizada en cada render
  const listaRecordatorios = generarListaRecordatorios();

  return (
    <>
      <form>
        {/* Reloj de cuenta regresiva */}
        <div className="row justify-content-center">
          {timeRemaining && (
            <Reloj time={timeRemaining} timepassed={timePassed} />
          )}
        </div>

        {/* Formulario rápido de horas y minutos */}
        <div className="row justify-content-center">
          <div className="col-md-8">
            <h5 className="text-center mb-2">
              <i className="fa fa-hourglass-start mr-2"></i>
              Temporizador
            </h5>
            <small className="form-text text-muted text-center d-block mb-3">
              Ingrese en cuántas horas y minutos desea recibir el recordatorio.
              La fecha se establece automáticamente (hoy).
            </small>

            <div className="row justify-content-center mb-3">
              <div className="form-group mr-3">
                <label htmlFor="horas">
                  <strong>Horas:</strong>
                </label>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: "100px", textAlign: "center" }}
                  step="1"
                  min="0"
                  max="24"
                  value={horas}
                  onInput={inputHoursHandler}
                />
              </div>
              <div className="form-group">
                <label htmlFor="minutos">
                  <strong>Minutos:</strong>
                </label>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: "100px", textAlign: "center" }}
                  step="1"
                  min="0"
                  max="59"
                  value={minutos}
                  onInput={inputMinutesHandler}
                />
              </div>
            </div>

            {/* Mostrar fecha/hora calculada */}
            {(+horas > 0 || +minutos > 0) && (
              <div className="text-center mb-3">
                <span className="badge badge-info" style={{ fontSize: "14px" }}>
                  <i className="fa fa-bell mr-1"></i>
                  Te avisaré a las:{" "}
                  {new Date(
                    Date.now() + (+horas * 60 + +minutos) * 60000
                  ).toLocaleTimeString("es-ES", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}{" "}
                  hrs
                </span>
              </div>
            )}

            <div className="form-group mb-3">
              <label htmlFor="asunto">
                Motivo <span style={{ color: "red" }}>*</span>
              </label>
              <textarea
                className="form-control"
                id="asunto"
                rows="3"
                value={asunto}
                placeholder="Ej: Verificar llegada a destino, Revisar documentación, etc."
                onChange={(e) => setAsunto(e.target.value)}
              />
            </div>

            <div className="text-center mb-3">
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={crearRecordatorioHandler}
                disabled={
                  (+horas === 0 && +minutos === 0) || !asunto.trim()
                }
              >
                <i className="fa fa-play mr-2"></i>
                Empezar
              </button>
            </div>
          </div>
        </div>

        {/* Lista de recordatorios - se actualiza en tiempo real */}
        {listaRecordatorios.length > 0 && (
          <div className="row justify-content-center">
            <div className="col-md-10">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6>
                  <i className="fa fa-list mr-1"></i>
                  Temporizadores:
                </h6>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={recargarHistorico}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fa fa-spinner fa-spin mr-1"></i>
                      Cargando...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-refresh mr-1"></i>
                      Actualizar Histórico
                    </>
                  )}
                </button>
              </div>
              <ul className="list-group">{listaRecordatorios}</ul>
            </div>
          </div>
        )}
      </form>
    </>
  );
};

/**
 * Componente principal que decide cuál versión renderizar
 * según el id de la plataforma.
 * - Plataforma 820: versión legacy (último commit en GitHub)
 * - Demás plataformas: versión nueva con temporizador mejorado
 */
const Recordatorio = (props) => {
  const idPlataforma = props.movimiento.plataforma
    ? props.movimiento.plataforma.id
    : props.movimiento.idPlataforma
      ? props.movimiento.idPlataforma.id
      : null;

  if (idPlataforma === 820) {
    return <RecordatorioLegacy {...props} />;
  }

  return <RecordatorioNuevo {...props} />;
};

export default Recordatorio;
